import React, { useMemo } from "react";
import { SectionHeader } from "../primitives";
import SubagencyCard from "../SubagencyCard";
import { selectionKey } from "../data";
import { colors } from "../../../utils/styles";

const btnSm = {
  padding: "6px 14px",
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer"
};

function DirectChargesStep({
  selection,
  subagenciesBySelection = {},
  subagencyData,
  entries,
  expanded,
  taxRollYear,
  onAdd,
  onRemove,
  onUpdate,
  onToggle,
  onExpandAll,
  onCollapseAll
}) {
  const subs = subagenciesBySelection[selectionKey(selection)] || [];
  // Memoize on a stringified key so Set identity is stable when *contents* are unchanged.
  // Without this, every entry edit (even unrelated fields) would bust SubagencyCard.memo on every card.
  const selectedCodesKey = entries
    .map((e) => e.subagency)
    .filter(Boolean)
    .sort()
    .join(",");
  const selectedCodes = useMemo(
    () => new Set(selectedCodesKey ? selectedCodesKey.split(",") : []),
    [selectedCodesKey]
  );
  const filledCount = selectedCodes.size;

  return (
    <div>
      <SectionHeader
        number="2"
        title="Direct Charges Detail"
        subtitle="Add one or more subagencies with charge details, legal authority, and contacts"
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
          padding: "12px 16px",
          background: colors.sectionBg,
          borderRadius: 8
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
          <span style={{ fontSize: 14, color: colors.textMuted }}>
            {entries.length} subagenc{entries.length === 1 ? "y" : "ies"} added
            {filledCount > 0 && (
              <>
                {" "}
                · <span style={{ color: colors.success }}>{filledCount} selected</span>
              </>
            )}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {entries.length > 1 && (
            <>
              <button
                onClick={onExpandAll}
                style={{
                  ...btnSm,
                  border: `1px solid ${colors.borderLight}`,
                  background: colors.white,
                  color: colors.textMuted
                }}
              >
                Expand All
              </button>
              <button
                onClick={onCollapseAll}
                style={{
                  ...btnSm,
                  border: `1px solid ${colors.borderLight}`,
                  background: colors.white,
                  color: colors.textMuted
                }}
              >
                Collapse All
              </button>
            </>
          )}
          <button
            onClick={onAdd}
            style={{
              ...btnSm,
              border: `1px solid ${colors.gold}`,
              background: colors.goldPale,
              color: colors.gold
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.gold;
              e.currentTarget.style.color = colors.white;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = colors.goldPale;
              e.currentTarget.style.color = colors.gold;
            }}
          >
            + Add Subagency
          </button>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {entries.map((entry, idx) => (
          <SubagencyCard
            key={entry.id}
            entry={entry}
            index={idx}
            total={entries.length}
            subs={subs}
            subagencyData={subagencyData}
            selectedCodes={selectedCodes}
            taxRollYear={taxRollYear}
            expanded={expanded.has(entry.id)}
            onToggle={onToggle}
            onUpdate={onUpdate}
            onRemove={onRemove}
          />
        ))}
      </div>

      <button
        onClick={onAdd}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          width: "100%",
          marginTop: 16,
          padding: "14px 0",
          borderRadius: 10,
          border: `2px dashed ${colors.border}`,
          background: "transparent",
          color: colors.textMuted,
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = colors.gold;
          e.currentTarget.style.color = colors.gold;
          e.currentTarget.style.background = colors.goldPale;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = colors.border;
          e.currentTarget.style.color = colors.textMuted;
          e.currentTarget.style.background = "transparent";
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <line x1="8" y1="3" x2="8" y2="13" />
          <line x1="3" y1="8" x2="13" y2="8" />
        </svg>
        Add Another Subagency
      </button>
    </div>
  );
}

export default React.memo(DirectChargesStep);
